<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
    fluid
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
        md="6"
      >
        <BFormGroup
          label="Activity"
          label-for="filterActivity"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tablePlans.selected.areaManagersActivity"
            placeholder="filter activity here"
            input-id="filterActivity"
            label="activity_name"
            clearable
            append-to-body
            :options="(tablePlans.options.areaManagersActivities)"
            :loading="(tablePlans.fetching.areaManagersActivities)"
            :disabled="(tablePlans.busy || tablePlans.fetching.areaManagersActivities)"
            :calculate-position="calculateDropPosition"
            @search="searchFilterAreaManagerActivities"
          >
            <template #option="{ activity_name }">
              <div
                class="text-break text-wrap"
                v-text="activity_name"
              />
            </template>
            <template #selected-option="{ activity_name }">
              <div
                class="w-225px w-md-250px w-lg-350px text-truncate"
                v-text="activity_name"
              />
            </template>
            <template #no-options="">
              no available activity
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>
    </BRow>

    <BRow>
      <BCol
        cols="12"
      >
        <BOverlay
          class="w-100 py-2"
          rounded
          opacity="0.1"
          spinner-variant="primary"
          :show="tablePlans.busy"
        >
          <full-calendar
            ref="fullCalendar"
            class="w-100 py-1 rounded-lg"
            :options="calendarOptions"
          >
            <template #eventContent="{ event, view }">
              <div
                v-if="event.extendedProps"
                class="d-flex flex-column rounded-lg position-relative"
              >
                <template v-if="view.type === 'listMonth'">
                  <ListMonthView
                    :event="event"
                    @view="editPlan"
                    @cancel="cancelPlan"
                  />
                </template>
                <template
                  v-else
                >
                  <GridMonthView
                    :event="event"
                    @view="editPlan"
                  />
                </template>
              </div>
            </template>
          </full-calendar>
        </BOverlay>
      </BCol>
    </BRow>

    <BModal
      ref="modalCreatePlan"
      size="lg"
      title="Plan"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      content-class="scrollable-tabs"
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
    >
      <BTabs
        active-nav-item-class="font-weight-bold text-uppercase text-primary"
        content-class="scrollable"
      >
        <BTab class="p-2">
          <template #title>
            <div class="text-md-nowrap">
              <i
                :class="['fa-xl fad fa-file-lines', {
                  'text-danger fat fa-shake': vTabPlanDetails()
                }]"
              />
              <b
                :class="['is-tab-active ml-1', {
                  'text-danger': vTabPlanDetails()
                }]"
              >
                Details
              </b>
            </div>
          </template>
          <ValidationObserver
            ref="formPlan"
          >
            <form
              role="form"
              novalidate
              @submit.prevent
            >
              <BRow>

                <BCol
                  cols="12"
                  md="3"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="event_date"
                    name="date"
                    rules="required"
                  >
                    <BFormGroup
                      label="Event Date"
                      label-for="fDate"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fDate"
                        v-model="formPlan.event_date"
                        type="date"
                        :state="errors.length > 0 ? false : null"
                        :min="formPlanValidation.eventDate.min"
                        :max="formPlanValidation.eventDate.max"
                        :disabled="state.busy || state.editing || !state.editable || Number(activityRules.is_deleted) === 1"
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol cols="12" />

                <BCol
                  cols="12"
                  md="9"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="activity_id"
                    name="activity"
                    rules="required"
                  >
                    <BFormGroup
                      label="Activity"
                      label-for="fActivity"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <VSelect
                        v-model="selected.areaManagersActivity"
                        input-id="fActivity"
                        label="activity_name"
                        append-to-body
                        placeholder="select activity here"
                        :options="options.areaManagersActivities"
                        :disabled="[
                          fetching.areaManagersActivities,
                          state.busy,
                          !state.editable,
                          Number(activityRules.is_deleted) === 1
                        ].some(isTrue => isTrue)"
                        :loading="fetching.areaManagersActivities"
                        :class="{ 'is-invalid': errors.length > 0 }"
                      >
                        <template #option="{ activity_name }">
                          <div
                            class="text-break text-wrap"
                            v-text="activity_name"
                          />
                        </template>
                        <template #selected-option="{ activity_name }">
                          <div
                            class="w-225px w-md-325px text-truncate"
                            v-text="activity_name"
                          />
                        </template>
                        <template #no-options="">
                          no available activity
                        </template>
                      </VSelect>
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="3"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="number_of_activities"
                    name="number of activities"
                    rules="required|min:0"
                  >
                    <BFormGroup
                      label="Number of Activities"
                      label-for="fNumberOfActivities"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fNumberOfActivities"
                        v-model="formPlan.number_of_activities"
                        type="number"
                        min="0"
                        placeholder="enter number of activities here"
                        :state="errors.length > 0 ? false : null"
                        :disabled="[
                          state.busy,
                          !state.editable
                        ].some(isTrue => isTrue)"
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol cols="12" />

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="province_id"
                    name="province"
                    rules="required"
                  >
                    <BFormGroup
                      label="Province"
                      label-for="fProvince"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <VSelect
                        v-model="selected.province"
                        input-id="fProvince"
                        label="province_name"
                        append-to-body
                        placeholder="select province here"
                        :options="options.provinces"
                        :disabled="[
                          state.busy,
                          fetching.provinces,
                          !state.editable
                        ].some(isTrue => isTrue)"
                        :loading="fetching.provinces"
                        :calculate-position="calculateDropPosition"
                        :class="{ 'is-invalid': errors.length > 0 }"
                        @search="searchProvinces"
                      >
                        <template #option="{ province_name, region }">
                          <div
                            class="text-break text-wrap"
                            v-text="province_name"
                          />
                          <div
                            v-if="region"
                            class="text-muted small"
                            v-text="region?.region_name"
                          />
                        </template>
                        <template #selected-option="{ province_name }">
                          <div
                            class="w-200px w-lg-300px text-truncate"
                            v-text="province_name"
                          />
                        </template>
                        <template #no-options="">
                          no available province
                        </template>
                      </VSelect>
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="municipality_id"
                    name="municipality"
                    rules="required"
                  >
                    <BFormGroup
                      label="Municipality"
                      label-for="fMunicipality"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <VSelect
                        v-model="selected.municipality"
                        input-id="fMunicipality"
                        label="municipality_name"
                        append-to-body
                        placeholder="select municipality here"
                        :options="options.municipalities"
                        :disabled="[
                          state.busy,
                          fetching.municipalities,
                          fetching.provinces,
                          !state.editable
                        ].some(isTrue => isTrue)"
                        :loading="fetching.municipalities"
                        :calculate-position="calculateDropPosition"
                        :class="{ 'is-invalid': errors.length > 0 }"
                        @search="searchMunicipalities"
                      >
                        <template #option="{ municipality_name }">
                          <div
                            class="text-break text-wrap"
                            v-text="municipality_name"
                          />
                        </template>
                        <template #selected-option="{ municipality_name }">
                          <div
                            class="w-200px w-lg-300px text-truncate"
                            v-text="municipality_name"
                          />
                        </template>
                        <template #no-options="">
                          no available municipality
                        </template>
                      </VSelect>
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="remarks"
                    name="remarks"
                    rules="max:1000"
                  >
                    <BFormGroup
                      label="Remarks"
                      label-for="fRemarks"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <template #label>
                        <span class="d-flex justify-content-between">
                          Remarks
                          <span>
                            {{ formPlan.remarks ? formPlan.remarks.length : 0 }} / 1000
                          </span>
                        </span>
                      </template>
                      <BFormTextarea
                        id="fRemarks"
                        v-model="formPlan.remarks"
                        :placeholder="state.editable ? 'enter remarks here ( optional )' : ''"
                        rows="7"
                        max-rows="9999"
                        maxlength="1000"
                        :state="errors.length > 0 ? false : null"
                        :disabled="state.busy || !state.editable || activityRules.is_deleted === 1"
                        @keydown.enter="preventEnterKey"
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>
              </BRow>
            </form>
          </ValidationObserver>
        </BTab>
      </BTabs>
      <template #modal-footer="{ cancel, visible }">
        <div class="w-100 d-flex justify-content-between align-items-center gap-2">
          <div
            v-if="state.editable && formPlan?.id"
          >
            <BButton
              variant="outline-danger"
              :disabled="state.busy"
              @click.prevent="cancelPlan(formPlan)"
            >
              Cancel
            </BButton>
          </div>
          <div />
          <div
            class="d-flex gap-2"
          >
            <BButton
              v-if="state.editable"
              variant="primary"
              :disabled="state.busy"
              @click.prevent="validatePlan"
            >
              {{ state.editing ? 'Update' : 'Add' }} Plan
            </BButton>
            <BButton
              v-if="visible"
              variant="outline-dark"
              :disabled="state.busy"
              @click.prevent="cancel()"
            >
              {{ state.editable ? 'Discard' : 'Close Window' }}
            </BButton>
          </div>
        </div>
      </template>
    </BModal>
  </BContainer>
</template>
<script>
import QUERY, { isEmpty } from 'lodash'
import { mapGetters } from 'vuex'
import MOMENT from 'moment'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'

import ListMonthView from './ListMonthView.vue'
import GridMonthView from './GridMonthView.vue'

import MISC from '@/mixins/misc'
import FORMATTER from '@/mixins/formatter'
import IMAGE_URL from '@/mixins/image-url'
import MAreaManagerActivities from '@/mixins/maintenances/area-managers-activities'
import MCustomers from '@/mixins/accounts/customers'
import MCrops from '@/mixins/maintenances/crops'
import MProvinces from '@/mixins/locations/provinces'
import MMunicipalities from '@/mixins/locations/municipalities'

import { SASMMonthlyPlan } from '@/services/asm'

// VSELECT MIXINS

// FULLCALENDAR PLUGINS
// import DayWithLists from '@/components/fullcalendar/fc.day-with-lists'

// import Attachment from './Attachment.vue'

export default {

  name: 'ASMToDosMonthlyPlansBase',

  components: {
    // Attachment,
    ListMonthView,
    GridMonthView
  },

  mixins: [
    MProvinces,
    MMunicipalities,
    MAreaManagerActivities,
    MCustomers,
    MCrops,
    MISC,
    FORMATTER,
    IMAGE_URL
  ],

  middleware: ['auth', 'asm'],

  metaInfo: () => ({
    title: 'Plans'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false,
        editable: true
      },
      selected: {
        activityType: 'Event'
      },
      activityRules: {
        customer_per_event: 0,
        customer: 0,
        is_deleted: 0,
        is_updated: 0
      },
      formPlanValidation: {
        eventDate: {
          min: '',
          max: ''
        }
      },
      formPlan: {
        id: '',
        event_date: '',
        schedule: '',
        activity_id: '',
        number_of_activities: '',
        province_id: '',
        municipality_id: '',
        remarks: '',
        active: 1
      },
      tablePlans: {
        busy: false,
        fetching: {
          areaManagersActivities: false
        },
        options: {
          areaManagersActivities: [],
          statuses: [
            { text: 'All', value: 'All' },
            { text: 'Pending', value: 'Pending' },
            { text: 'Uncompleted', value: 'Not Completed' },
            { text: 'Completed', value: 'Completed' },
            { text: 'Canceled', value: 'Canceled' }
          ]
        },
        selected: {
          activityType: 'Event',
          areaManagersActivity: '',
          schedule: ''
        },
        filter: {
          status: 'All',
          created_by: 'Anyone',
          activity: ''
        },
        rows: []
      },
      calendarOptions: {
        timeZone: 'Asia/Taipei',
        customButtons: {
          addPlanButton: {
            text: 'Add Plan',
            click: () => {
              this.createPlan({
                date: MOMENT().format('YYYY-MM-DD')
              })
            }
          }
        },
        loading: isLoading => {
          this.tablePlans.busy = isLoading
        },
        plugins: [
          dayGridPlugin,
          listPlugin,
          timeGridPlugin,
          interactionPlugin
        ],
        headerToolbar: this.isMobile() ? {
          left: 'prev,today,next dayGridMonth,listMonth',
          right: 'addPlanButton title'
        } : {
          left: 'addPlanButton',
          center: 'title',
          right: 'prev,today,next dayGridMonth,dayGridWeek,listMonth'
        },
        validRange: {
          start: '',
          end: ''
        },
        initialView: this.isMobile() ? 'listMonth' : 'dayGridMonth',
        views: {
          dayGridMonth: {
            buttonText: 'Month'
          },
          dayGridWeek: {
            buttonText: 'Week'
          },
          listMonth: {
            buttonText: 'Schedule'
          }
        },
        moreLinkClick: 'listMonth', // popover, viewName
        moreLinkContent: planLists => `View ${planLists.num} plans`,
        initialEvents: [],
        dayMaxEvents: true,
        eventMaxStack: 3,
        editable: false,
        droppable: false,
        selectMirror: false,
        weekends: true,
        events: this.eventProvider,
        dateClick: this.createPlan,
        eventClick: this.editPlan
      },
      calendarApi: null
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    })
  },

  watch: {
    'selected.areaManagersActivity' (areaManagersActivity) {
      this.formPlan.activity_id = areaManagersActivity?.id || ''

      const activityRules = areaManagersActivity?.rules || {}

      this.activityRules = {
        customer_per_event: activityRules?.customer_per_event || 0,
        customer: activityRules?.customer || 0,
        is_deleted: 0,
        is_updated: 0
      }

      this.selected.customer = ''
    },

    'tablePlans.selected.schedule' () {
      this.getAreaManagerActivities(this.tablePlans)
    },

    'selected.schedule' () {
      this.getAreaManagerActivities(this)
    },

    'formPlan.event_date' (eventDate) {
      this.formPlan.schedule = eventDate ? MOMENT(eventDate).format('YYYY-MM') : ''
      this.selected.schedule = eventDate ? MOMENT(eventDate).format('YYYY-MM') : ''
    },

    'selected.province' (province) {
      this.formPlan.province_id = province?.id || ''
    },

    'selected.municipality' (municipality) {
      this.formPlan.municipality_id = municipality?.id || ''
    },

    'tablePlans.selected.areaManagersActivity' (areaManagersActivity) {
      this.tablePlans.filter.activity = areaManagersActivity?.id || ''

      this.refetchEvents()
    }
  },

  mounted () {
    if (this.currentUser) {
      this.$nextTick(() => {
        this.calendarApi = this.$refs.fullCalendar?.getApi()
      })
    }
  },

  methods: {
    vTabPlanDetails () {
      if (this.$refs.formPlan) {
        return Object.keys(this.$refs.formPlan.errors).some(
          key => this.$refs.formPlan.errors[key]?.length > 0
        )
      }

      return false
    },

    async eventProvider (calendar, validData, invalidData) {
      const currentMonth = MOMENT(calendar.startStr)

      const isStartOfCalendarEndOfLastMonth = Number(currentMonth.format('D')) >= 20
      const schedule = isStartOfCalendarEndOfLastMonth ? currentMonth.add(1, 'month').format('YYYY-MM') : currentMonth.format('YYYY-MM')

      this.tablePlans.selected.schedule = schedule

      const payload = {
        schedule,
        activity_id: this.tablePlans.filter.activity
      }

      return await SASMMonthlyPlan.get(payload).then(
        ({ data }) => {
          const events = data.filter(
            item => isEmpty(item.deleted_at) || (!isEmpty(item.deleted_at) && MOMENT(item.event_date).isBefore(MOMENT(item.deleted_at)))
          ).map(
            item => {
              const eventDate = this.dateFormatter(item.event_date)

              return ({
                id: item.id,
                title: item?.activity?.activity_name || '',
                start: this.dateTimeLocalFormatter(`${eventDate} 08:00:00`),
                end: this.dateTimeLocalFormatter(`${eventDate} 17:00:00`),
                extendedProps: item,
                allDay: true,
                editable: false,
                droppable: false
              })
            }
          )

          this.tablePlans.rows = events

          validData(events)
        }
      ).catch(() => {
        invalidData([])
      })
    },

    searchFilterAreaManagerActivities (search, loading) {
      this.searchAreaManagerActivitiesProvider(this.tablePlans, search, loading)
    },

    refetchEvents () {
      this.calendarApi.refetchEvents()
    },

    createPlan ({ date }) {
      const currentDate = this.dateFormatter(date)
      const today = MOMENT().format('YYYY-MM-DD')

      if (MOMENT(currentDate).isBefore(today, 'day')) {
        return
      }

      const schedule = MOMENT(currentDate).format('YYYY-MM')

      const dateFormat = 'YYYY-MM-DD'
      const currentMonth = `${schedule}-01`
      const month = MOMENT(MOMENT(currentMonth).format(dateFormat))

      this.formPlanValidation.eventDate = {
        min: today,
        max: month.endOf('month').format(dateFormat)
      }

      this.selected.schedule = schedule

      this.state.editing = false
      this.state.editable = true

      this.editable.municipality = ''

      this.formPlan = Object.assign(this.formPlan, {
        id: '',
        event_date: currentDate,
        schedule: schedule,
        activity_id: '',
        number_of_activities: 1,
        province_id: '',
        municipality_id: '',
        remarks: '',
        active: 1
      })

      this.getAreaManagerActivities(this).finally(() => {
        this.getProvinces(this)
      })

      this.$refs.modalCreatePlan.show()
    },

    editPlan ({ event: { extendedProps: plan }, view }) {
      if (view.type === 'listMonth') {
        return
      }

      const currentDate = this.dateFormatter(plan.event_date)
      const today = MOMENT().format('YYYY-MM-DD')

      if (MOMENT(currentDate).isBefore(today, 'day') || !isEmpty(plan?.deleted_at)) {
        this.state.editable = false
      } else {
        this.state.editable = true
      }

      const dateFormat = 'YYYY-MM-DD'
      const schedule = MOMENT(currentDate)

      this.formPlanValidation.eventDate = {
        min: '',
        max: schedule.endOf('month').format(dateFormat)
      }

      this.state.editing = true

      this.editable.municipality = plan?.municipality_id || ''

      this.selected.schedule = plan.schedule

      this.formPlan = Object.assign(this.formPlan, {
        id: plan?.id,
        event_date: this.dateFormatter(plan.event_date),
        schedule: plan.schedule,
        activity_id: '',
        number_of_activities: plan.new_number_of_activities,
        province_id: '',
        municipality_id: '',
        remarks: plan.remarks,
        active: plan.active
      })

      this.getAreaManagerActivities(this).finally(() => {
        this.selected.areaManagersActivity = QUERY.find(this.options.areaManagersActivities, {
          id: plan?.activity_id
        }) || ''

        this.getProvinces(this).finally(() => {
          this.selected.province = QUERY.find(this.options.provinces, {
            id: plan?.province_id
          }) || ''
        })
      })

      this.$refs.modalCreatePlan.show()
    },

    async validatePlan () {
      await this.$refs.formPlan.validate().then(
        async validPlan => {
          if (!validPlan) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Plan?</h6>' : '<h6>Add this Plan?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Add',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putPlan() : this.postPlan()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  if (message & message.errors) {
                    this.$refs.formPlan.setErrors(message.errors)
                  }
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postPlan () {
      return SASMMonthlyPlan.post(this.formPlan).then(
        ({ data }) => {
          this.swalSuccess(data.message).then(() => {
            this.$refs.modalCreatePlan.hide()

            this.refetchEvents()
          })

          return data
        }
      )
    },

    async validatePlanStatus (status = 'Pending') {
      await this.$refs.formPlan.validate().then(
        validPlan => {
          if (!validPlan) {
            return this.swalInvalid()
          }

          const isCompleted = status === 'Completed'

          this.swalConfirmWithRemarks({
            html: isCompleted ? '<h6>Mark this Plan as Completed?</h6>' : '<h6>Mark this Plan as Not Completed?</h6>',
            confirmButtonText: isCompleted ? 'Mark as Completed' : 'Mark as Not Completed',
            customClass: {
              confirmButton: isCompleted ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-danger'
            },
            remarksRequired: true,
            preConfirm: async remarks => {
              this.state.busy = true

              this.formPlan = Object.assign(this.formPlan, {
                edit_mode: 0,
                remarks
              })

              const createOrUpdate = async () => {
                return this.putPlan()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  if (message & message.errors) {
                    this.$refs.formPlan.setErrors(message.errors)
                  }
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async putPlan () {
      return SASMMonthlyPlan.put(this.formPlan).then(
        ({ data }) => {
          this.swalSuccess(data.message).then(() => {
            this.$refs.modalCreatePlan.hide()

            this.refetchEvents()
          })

          return data
        }
      )
    },

    cancelPlan (plan) {
      this.swalConfirm({
        html: '<h6>Mark this Plan as Canceled?</h6>',
        confirmButtonText: 'Mark as Canceled',
        cancelButtonText: 'Keep',
        customClass: {
          confirmButton: 'btn btn-sm btn-danger'
        },
        remarksRequired: true,
        preConfirm: async () => {
          this.state.busy = true

          return SASMMonthlyPlan.delete({ id: plan.id }).then(
            ({ data }) => {
              this.swalSuccess(data.message).then(() => {
                this.$refs.modalCreatePlan.hide()

                this.refetchEvents()
              })

              return data
            }
          ).finally(() => {
            this.state.busy = false
          })
        }
      })
    }
  }
}
</script>
